import { useEffect, useCallback } from "react";
import { useRecoilState } from "recoil";
import { useMutation } from "@apollo/client";
import { MY_HR_REFRESH_TOKEN } from "my-hr/graphql";
import { useOcCurrentUserState } from "oc/hooks";
import { ocRefreshTokenTimestampState, ocSessionPeriodState } from "states";
import { ocRefreshTokenState } from "states";

export const useOcRefreshToken = ({ isLoggedIn }: { isLoggedIn: boolean }) => {
  let {
    accessTokenPeriod,
    accessToken,
    setData: setCurrentUser,
  } = useOcCurrentUserState();
  const [sessionPeriod, setSessionPeriod] =
    useRecoilState(ocSessionPeriodState);
  const [refreshTokenError, setRefreshTokenError] =
    useRecoilState(ocRefreshTokenState);

  const [refreshTokenTimestamp, setRefreshTokenTimestamp] = useRecoilState(
    ocRefreshTokenTimestampState,
  );

  const [refreshTokenMutation] = useMutation(MY_HR_REFRESH_TOKEN, {
    fetchPolicy: "network-only",
    variables: { accessToken },
  });

  const refreshToken = useCallback(async () => {
    try {
      const { data } = await refreshTokenMutation();
      const result = data?.refreshToken;
      let now = new Date().toUTCString();
      setRefreshTokenTimestamp(now);
      if (refreshTokenError) {
        setRefreshTokenError(false);
      }

      if (
        result.accessTokenPeriod !== accessTokenPeriod ||
        result.accessToken !== accessToken
      ) {
        setCurrentUser({
          accessTokenPeriod: result.accessTokenPeriod,
          accessToken: result.accessToken,
        });
      }
      if (result.sessionPeriod !== sessionPeriod) {
        setSessionPeriod(result.sessionPeriod);
      }
    } catch (err) {
      console.log({ err });
      // Surface the failure as state so the useEffect retry-flow can
      // re-attempt on the next render / interval tick. Without this,
      // a transient mutation failure would silently consume the
      // error and never be retried.
      setRefreshTokenError(true);
    }
  }, [
    refreshTokenMutation,
    setRefreshTokenTimestamp,
    refreshTokenError,
    setRefreshTokenError,
    accessTokenPeriod,
    accessToken,
    setCurrentUser,
    sessionPeriod,
    setSessionPeriod,
  ]);

  const refreshInTime = ({
    accessToken,
    accessTokenPeriod,
    refreshTokenTimestamp,
  }: {
    accessToken: string;
    accessTokenPeriod: number;
    refreshTokenTimestamp: string;
  }) => {
    // Skip while the browser knows it's offline — the GraphQL mutation
    // would just produce a `Failed to fetch` every 10s. When connectivity
    // returns the next interval tick (within 10s) runs the check again.
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    if (accessToken && accessTokenPeriod) {
      let now = new Date();
      let lastRefresh = new Date(refreshTokenTimestamp);
      let difference = now.getTime() - lastRefresh.getTime();
      let refreshTokenPeriod = 1000 * 60 * accessTokenPeriod - 15000;
      if (difference > refreshTokenPeriod) {
        refreshToken();
      }
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      if (refreshTokenError) {
        refreshToken();
      }

      let timer = setInterval(() => {
        refreshInTime({
          accessToken,
          accessTokenPeriod,
          refreshTokenTimestamp,
        });
      }, 1000 * 10);
      return () => {
        clearInterval(timer);
      };
    }
    // eslint-disable-next-line
  }, [
    refreshTokenError,
    isLoggedIn,
    refreshTokenTimestamp,
    accessToken,
    accessTokenPeriod,
  ]);
};

export default useOcRefreshToken;
